import { env } from "~/config/env";
import { STORAGE_KEYS, PLATFORM_HEADER } from "~/config/constants";

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public errorCode?: string,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

export interface RequestOptions {
  /** Set to false for login calls, where a 401 is a rejected login rather than an expired session. */
  redirectOnUnauthorized?: boolean;
}

class HttpClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = env.API_BASE_URL;
  }

  private getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  }

  private buildHeaders(customHeaders?: Record<string, string>): Headers {
    const headers = new Headers(customHeaders);

    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    headers.set("platform", PLATFORM_HEADER);

    const token = this.getToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  }

  private async handleResponse<T>(
    response: Response,
    { redirectOnUnauthorized = true }: RequestOptions = {},
  ): Promise<T> {
    if (response.status === 401 && redirectOnUnauthorized) {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      window.location.href = "/login";
      throw new ApiRequestError("Unauthorized", 401);
    }

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new ApiRequestError(
        data.error?.message || "Request failed",
        response.status,
        data.error?.code,
      );
    }

    return data.data as T;
  }

  async get<T>(
    path: string,
    params?: Record<string, string | number | undefined>,
  ): Promise<T> {
    const url = new URL(`${this.baseUrl}${path}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      });
    }

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: this.buildHeaders(),
    });

    return this.handleResponse<T>(response);
  }

  async post<T>(
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: "POST",
      headers: this.buildHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response, options);
  }

  async patch<T>(path: string, body?: unknown): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: "PATCH",
      headers: this.buildHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async put<T>(path: string, body?: unknown): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: "PUT",
      headers: this.buildHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async delete<T>(path: string): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: "DELETE",
      headers: this.buildHeaders(),
    });

    return this.handleResponse<T>(response);
  }

  async uploadFile(path: string, file: File): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);

    const headers = new Headers();
    headers.set("platform", PLATFORM_HEADER);
    const token = this.getToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      method: "POST",
      headers,
      body: formData,
    });

    return this.handleResponse<string>(response);
  }
}

export const httpClient = new HttpClient();
